import { useEffect, useRef } from "react";

import useLatestRef from "hooks/use-latest-ref.hook";
import type { Mention } from "services/types/mentions.type";

/** "301,302": the ids of `currentUserId`'s mentions that are not seen yet, sorted. */
export const getUnseenMentionKey = (
  mentions: Mention[] | undefined,
  currentUserId: number | undefined
): string =>
  currentUserId
    ? (mentions ?? [])
        .filter(
          (mention) => mention.user.id === currentUserId && !mention.seen_at
        )
        .map((mention) => mention.id)
        .sort((a, b) => a - b)
        .join(",")
    : "";

/**
 * When someone who was @mentioned opens the thing they were mentioned in, record that they saw it
 * — it drives the writer's "Seen" line and marks the bell notification read. Sent once per
 * (target, set of unseen ids), so a new mention arriving while it is open is marked too. A failed
 * call is retried on the next change; it is not worth interrupting the user over.
 *
 * `unseenKey` is anything that changes when there is something new to mark ("" = nothing).
 * `markSeen` must resolve or reject (pass an RTK mutation's `.unwrap()`).
 */
export default function useMarkMentionsSeen({
  enabled,
  targetId,
  unseenKey,
  markSeen
}: {
  enabled: boolean;
  targetId: number | undefined;
  unseenKey: string;
  markSeen: (targetId: number) => Promise<unknown>;
}) {
  const sentForKeyRef = useRef<string | null>(null);
  const markSeenRef = useLatestRef(markSeen);

  const sendKey = unseenKey && targetId ? `${targetId}:${unseenKey}` : null;

  useEffect(() => {
    if (!enabled || !sendKey || !targetId) return;
    if (sentForKeyRef.current === sendKey) return;
    sentForKeyRef.current = sendKey;
    markSeenRef.current(targetId).catch(() => {
      sentForKeyRef.current = null;
    });
  }, [enabled, markSeenRef, sendKey, targetId]);
}
